import { Button } from "@/components/ui/button";
import AdminPrivateLayout from "@/layouts/AdminPrivateLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Eye, CalendarCheck, Search, Loader2, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { useState, useEffect } from "react";
import { formatDate, formatTime, getCurrencyIcon, getRequestOptions } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

// --- TYPES ---
type Client = {
    id: string;
    name: string;
    email: string;
    totalBookings: number;
};

type Booking = {
    id: string;
    className: string;
    date: string;
    duration: number;
    price: number;
    currency: string;
    status: 'Booked' | 'Canceled';
};

// --- CONSTANTS ---
const CLIENTS_PER_PAGE = 25;
const BOOKINGS_PER_PAGE = 25;

// --- PAGINATION COMPONENT (Unchanged) ---
const PaginationControls = ({ currentPage, totalPages, onPageChange }: { currentPage: number, totalPages: number, onPageChange: (page: number) => void }) => {
    const { t } = useTranslation();

    const pageNumbers: number[] = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
    }

    return (
        <div className="flex items-center justify-between px-4 py-3 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
                <Button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    variant="outline"
                    size="sm"
                >
                    {t('pagination.previous')}
                </Button>
                <Button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    variant="outline"
                    size="sm"
                >
                    {t('pagination.next')}
                </Button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-center">
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <Button
                        onClick={() => onPageChange(1)}
                        disabled={currentPage === 1}
                        variant="ghost"
                        size="icon"
                        className="rounded-r-none"
                    >
                        <ChevronsLeft className="h-4 w-4" />
                    </Button>
                    <Button
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        variant="ghost"
                        size="icon"
                        className="rounded-none"
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </Button>

                    {pageNumbers.map(page => (
                        <Button
                            key={page}
                            onClick={() => onPageChange(page)}
                            variant={page === currentPage ? "default" : "outline"}
                            size="sm"
                            className={`z-10 ${page === currentPage ? 'bg-primary text-primary-foreground' : 'text-gray-700 bg-white hover:bg-gray-50'}`}
                        >
                            {page}
                        </Button>
                    ))}

                    <Button
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        variant="ghost"
                        size="icon"
                        className="rounded-none"
                    >
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                        onClick={() => onPageChange(totalPages)}
                        disabled={currentPage === totalPages}
                        variant="ghost"
                        size="icon"
                        className="rounded-l-none"
                    >
                        <ChevronsRight className="h-4 w-4" />
                    </Button>
                </nav>
            </div>
        </div>
    );
};


// --- CLIENT BOOKINGS DIALOG COMPONENT ---

const ClientBookingsList = ({ clientId, clientName }: { clientId: string, clientName: string }) => {
    const { t } = useTranslation();

    // States for Server-Side Pagination (Typed)
    const [currentPage, setCurrentPage] = useState(1);
    const [bookedClasses, setBookedClasses] = useState<Booking[]>([]);
    const [totalBookings, setTotalBookings] = useState(0);
    const [isLoadingBookings, setIsLoadingBookings] = useState(false);

    const totalPages = Math.ceil(totalBookings / BOOKINGS_PER_PAGE);

    // Simulated Server-Side API Call for Bookings (Typed return)
    const fetchBookingsPage = async (): Promise<{ bookings: Booking[], totalCount: number }> => {
        setIsLoadingBookings(true);
        const startIndex = (currentPage - 1) * BOOKINGS_PER_PAGE;
        const response = await fetch(`${import.meta.env.VITE_BOOKING_CLIENT_ENDPOINT}/${clientId}/bookings?pageNumber=${currentPage}&pageSize=${BOOKINGS_PER_PAGE}`, getRequestOptions());
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log("Fetched bookings data:", data);
        setIsLoadingBookings(false);
        return {
            bookings: data.items as Booking[],
            totalCount: data.totalCount ?? 0,
        };
    };

    // Effect to fetch data whenever clientId or currentPage changes
    useEffect(() => {
        const loadBookings = async () => {
            const data = await fetchBookingsPage();
            setBookedClasses(data.bookings);
            setTotalBookings(data.totalCount);
        };

        loadBookings();
    }, [clientId, currentPage]);

    // Reset page when client changes
    useEffect(() => {
        setCurrentPage(1);
    }, [clientId]);


    return (
        // The main container for the dialog content
        <div className="flex flex-col h-full space-y-4">
            <DialogHeader className="flex-shrink-0">
                <DialogTitle className="text-xl">{t('clients.bookingsTitle', { name: clientName })}</DialogTitle>
                <DialogDescription>
                    {t('clients.bookingsDescription')}
                </DialogDescription>
            </DialogHeader>

            {/* Scrollable Content Area with fixed height structure to prevent resizing */}
            <div className="flex-grow min-h-0 overflow-y-auto max-h-[70vh] flex flex-col">

                {/* Table Content Area - uses min-h- to prevent dialog collapse during loading */}
                {isLoadingBookings ? (
                    <div className="flex flex-col items-center justify-center h-full min-h-[150px] p-6">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                        <p className="mt-2 text-gray-500">{t('clients.loading')}</p>
                    </div>
                ) : bookedClasses.length > 0 ? (
                    <div className="border rounded-lg flex-shrink-0">
                        <Table>
                            <TableHeader className="sticky top-0 bg-white shadow-sm z-10">
                                <TableRow>
                                    <TableHead>{t('clients.table.class')}</TableHead>
                                    <TableHead>{t('clients.table.date')}</TableHead>
                                    <TableHead>{t('clients.table.time')}</TableHead>
                                    <TableHead>{t('clients.table.duration')}</TableHead>
                                    <TableHead>{t('clients.table.price')}</TableHead>
                                    <TableHead>{t('clients.table.status')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {bookedClasses.map((booking) => (
                                    <TableRow key={booking.id} className={booking.status === 'Canceled' ? 'opacity-60' : ''}>
                                        <TableCell className="font-medium">{booking.className}</TableCell>
                                        <TableCell>{formatDate(booking.date)}</TableCell>
                                        <TableCell>{formatTime(booking.date)}</TableCell>
                                        <TableCell>{booking.duration} {t('bookingSummary.min')}</TableCell>
                                        <TableCell className="text-gray-600">
                                            <span className="inline-flex items-center">
                                                {getCurrencyIcon({ currencyCode: booking.currency, size: 4 })}
                                                <span>{booking.price}</span>
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${booking.status === 'Booked' ? 'bg-green-100 text-green-800' :
                                                booking.status === 'Canceled' ? 'bg-red-100 text-red-800' :
                                                    'bg-yellow-100 text-yellow-800'
                                                }`}>
                                                {t(`clients.status.${booking.status.toLowerCase()}`)}
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                ) : (
                    <div className="text-center p-6 border rounded-lg bg-gray-50 flex items-center justify-center h-full">
                        <CalendarCheck className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-gray-500">{t('clients.noBookings')}</p>
                    </div>
                )}

                {/* Pagination Placement (inside the scrollable container, pushed to bottom) */}
                {totalPages > 1 && !isLoadingBookings && (
                    <div className="flex-shrink-0 pt-4 mt-auto">
                        <PaginationControls
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />
                    </div>
                )}
            </div>

        </div>
    );
};


// --- MAIN CLIENTS PAGE COMPONENT ---

const Clients = () => {
    const { t } = useTranslation();
    const { toast } = useToast();

    // Dialog States
    const [isBookingsDialogOpen, setIsBookingsDialogOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState<Client | null>(null);

    // Client List States (Typed)
    const [searchInput, setSearchInput] = useState('');
    const [allFetchedClients, setAllFetchedClients] = useState<Client[]>([]); // 💡 Type: Client[]
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalClients, setTotalClients] = useState(0);
    const totalPages = Math.ceil(totalClients / CLIENTS_PER_PAGE);

    const fetchClients = async (query: string): Promise<{ clients: Client[], totalCount: number }> => {
        setIsLoading(true);

        try {
            const response = await fetch(`${import.meta.env.VITE_BOOKING_CLIENT_ENDPOINT}?query=${encodeURIComponent(query)}&pageNumber=${currentPage}&pageSize=${CLIENTS_PER_PAGE}`, getRequestOptions());

            if (!response.ok) {
                // Read error message from body if available
                const errorText = await response.text();
                throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
            }
            const data = await response.json();
            setIsLoading(false);
            return {
                clients: data.items as Client[],
                totalCount: data.totalCount ?? 0,
            };

        } catch (error) {
            console.error("Client fetch error:", error);
            setAllFetchedClients([]);
            setTotalClients(0);
            return { clients: [], totalCount: 0 };
        } finally {
            setIsLoading(false);
        }
    };

    // Search Trigger Function (Error is now resolved due to fetchClients typing)
    const handleSearch = async () => {
        setCurrentPage(1);

        if (searchInput.trim().length > 0 && searchInput.trim().length < 3) {
            toast({
                title: t("clients.searchError.title"),
                description: t("clients.minLengthWarning"),
                duration: 3000,
                className: "bg-red-500 text-white border-red-600",
            });
            setIsLoading(false);
            return;
        }

        try {
            const results = await fetchClients(searchInput.trim());
            setAllFetchedClients(results.clients);
            setTotalClients(results.totalCount);
        } catch (error) {
            console.error("Client search error:", error);
            toast({
                title: t("clients.searchError.apiTitle"),
                description: t("clients.searchError.apiDescription"),
                duration: 5000,
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Key Press Handler
    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearch();
        }
    };

    // Initial Load
    useEffect(() => {
        const loadClients = async () => {
            let trimmedInput = searchInput.trim();
            if (trimmedInput.length > 0 && trimmedInput.length < 3) {
                trimmedInput = '';
            }
            const data = await fetchClients(trimmedInput.trim());
            setAllFetchedClients(data.clients);
            setTotalClients(data.totalCount);
        };

        loadClients();
    }, [currentPage]);

    // Other handlers
    const handleViewClasses = (client: Client) => {
        setSelectedClient(client);
        setIsBookingsDialogOpen(true);
    };

    return (
        <AdminPrivateLayout>
            <div className="space-y-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 className="text-2xl font-bold text-gray-900">{t('clients.title')}</h2>
                <p className="text-gray-500 px-1">{t('clients.description')}</p>

                <Card className="shadow-lg">
                    {/* <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-xl font-semibold">
                        </CardTitle>
                    </CardHeader> */}
                    <CardContent className="p-0">
                        {/* Search Input and Button Group */}
                        <div className="p-4 border-b">
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                                <div className="relative flex-grow">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                                    <Input
                                        type="text"
                                        placeholder={t('clients.searchPlaceholder')}
                                        className="pl-10 pr-4 h-10 w-full"
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                        onKeyDown={handleKeyPress}
                                    />
                                </div>
                                <Button
                                    onClick={handleSearch}
                                    disabled={isLoading || (searchInput.trim().length > 0 && searchInput.trim().length < 3)}
                                    className="w-full sm:w-auto h-10"
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            {t('clients.searching')}
                                        </>
                                    ) : (
                                        t('clients.searchButton')
                                    )}
                                </Button>
                            </div>
                            {searchInput.trim().length > 0 && searchInput.trim().length < 3 && (
                                <p className="text-sm text-yellow-600 mt-2">{t('clients.minLengthWarning')}</p>
                            )}
                        </div>

                        {/* Client List Table */}
                        <div className="overflow-x-auto">
                            <Table className="min-w-full divide-y divide-gray-200">
                                <TableHeader className="bg-gray-50">
                                    <TableRow>
                                        <TableHead className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                                            {t('clients.table.name')}
                                        </TableHead>
                                        <TableHead className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell w-1/3">
                                            {t('clients.table.email')}
                                        </TableHead>
                                        <TableHead className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell w-1/6">
                                            {t('clients.table.totalBookings')}
                                        </TableHead>
                                        <TableHead className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                                            {t('clients.table.actions')}
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody className="bg-white divide-y divide-gray-200">
                                    {isLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={4} className="h-24 text-center">
                                                <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" />
                                                <p className="mt-2 text-gray-500">{t('clients.loading')}</p>
                                            </TableCell>
                                        </TableRow>
                                    ) : allFetchedClients?.length > 0 ? (
                                        allFetchedClients.map((client) => (
                                            <TableRow key={client.id} className="hover:bg-gray-50">
                                                <TableCell className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                                                    {client.name}
                                                </TableCell>
                                                <TableCell className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden sm:table-cell">
                                                    {client.email}
                                                </TableCell>
                                                <TableCell className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right hidden md:table-cell">
                                                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800">
                                                        {client.totalBookings}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <Button
                                                        variant="secondary"
                                                        size="sm"
                                                        onClick={() => handleViewClasses(client)}
                                                        className="inline-flex items-center gap-1"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={4} className="h-24 text-center text-gray-500">
                                                {t('clients.noClientsFound')}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>

                        {/* Client List Pagination */}
                        {totalPages > 1 && !isLoading && (
                            <PaginationControls
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                            />
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Dialog for Booked Classes */}
            <Dialog open={isBookingsDialogOpen} onOpenChange={setIsBookingsDialogOpen}>
                <DialogContent className="sm:max-w-xl md:max-w-3xl flex flex-col max-h-[90vh]">
                    {selectedClient && (
                        <ClientBookingsList
                            clientId={selectedClient.id}
                            clientName={selectedClient.name}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </AdminPrivateLayout>
    );
};

export default Clients;